/**
 * Lazy loader for the OddBird CSS Anchor Positioning polyfill.
 *
 * Native support: Chrome 125+, Firefox 149+, Safari 26+.
 *
 * The polyfill does not observe mutations, so dynamically mounted anchors
 * (e.g. each `Tooltip` generating a unique `anchorName` via `useId()`)
 * need to call `applyAnchorPositioningPolyfill` themselves after mount.
 * On browsers with native support the call is a no-op.
 */

type PolyfillFn = (options?: {
  elements?: HTMLElement[];
  excludeInlineStyles?: boolean;
  roots?: (Document | HTMLElement | ShadowRoot)[];
  useAnimationFrame?: boolean;
}) => Promise<unknown>;

const supportsNativeAnchorPositioning =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("anchor-name: --x");

let polyfillPromise: Promise<PolyfillFn> | null = null;

function loadPolyfill(): Promise<PolyfillFn> {
  polyfillPromise ??= import(
    /* webpackChunkName: "css-anchor-positioning-polyfill" */
    "@oddbird/css-anchor-positioning/fn"
  ).then((mod) => mod.default as PolyfillFn);
  return polyfillPromise;
}

export async function applyAnchorPositioningPolyfill(elements?: HTMLElement[]): Promise<void> {
  if (supportsNativeAnchorPositioning) {
    return;
  }
  const polyfill = await loadPolyfill();
  await polyfill(elements ? { elements } : undefined);
}
